import React from 'react';
import { DSBadge, DSIcon } from '../design-system/primitives';

export const IMPACT_CONFIG = {
  game_down: { label: 'Game Down', bg: '#2a0e0e', border: '#7f1d1d', text: '#ff6b6b', dot: '#ff3b3b' },
  tournament_risk: { label: 'Tournament Risk', bg: '#271c06', border: '#7c4a00', text: '#ffb020', dot: '#ffb020' },
  observation: { label: 'Observation', bg: '#181b2a', border: '#2d3148', text: '#7b82a6', dot: '#4a5070' }
};

export const IMPACT_ORDER = ['game_down', 'tournament_risk', 'observation'];

// Tickets pending triage don't have a required_tech_tier yet, but in practice
// triage almost always lands on T3 (Repair Tech) - this is what the triage
// form itself defaults to. Surfacing it as an "assumed" tier ahead of time
// lets techs gauge/accept a ticket without waiting for someone to triage it.
export const DEFAULT_TRIAGE_TIER = 3;

export const TIER_NAMES = {
  0: 'No Access',
  1: 'Glass Access',
  2: 'Routine Maintenance',
  3: 'Repair Tech',
  4: 'Senior Tech'
};

export const TIER_COLORS = {
  0: '#4a5070',
  1: '#2c6fa0',
  2: '#2e7d5a',
  3: '#6d4fb0',
  4: '#b45309'
};

export function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor(diff / 60000);
  if (days > 7) return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${Math.max(minutes, 0)}m ago`;
}

export function isNeedsTriage(issue) {
  return issue.required_tech_tier === null || issue.required_tech_tier === undefined;
}

export function viewerQualifies(issue, viewerTier, viewerIsAdmin) {
  if (viewerIsAdmin) return true;
  if (isNeedsTriage(issue)) return false;
  // Floor is Tier 3, not Tier 2 (2026-08-06 correction, requirements §18 item
  // 15): Tier 2 ("Routine Maintenance") doesn't work the reported-ticket
  // queue at all, regardless of a given ticket's Required Tier.
  return viewerTier >= 3 && viewerTier >= issue.required_tech_tier;
}

export function ImpactBadge({ impact, size = 'sm' }) {
  const c = IMPACT_CONFIG[impact] || IMPACT_CONFIG.observation;
  return (
    <span
      className={`tu-impact-badge tu-impact-badge--${size}`}
      style={{ background: c.bg, borderColor: c.border, color: c.text }}
    >
      <span className="tu-impact-badge__dot" style={{ background: c.dot }} />
      {c.label}
    </span>
  );
}

export function TierBadge({ tier, size = 'sm', assumed = false }) {
  const hasTier = tier !== null && tier !== undefined;
  const color = hasTier ? (TIER_COLORS[tier] || '#4a5070') : '#4a5070';
  const label = hasTier ? `T${tier}` : '?';
  const title = hasTier
    ? (assumed ? `Assumed Tier ${tier} - ${TIER_NAMES[tier]} (pending triage)` : `Tier ${tier} - ${TIER_NAMES[tier]}`)
    : 'Needs Triage';
  return (
    <span
      className={`tu-tier-badge tu-tier-badge--${size} ${assumed ? 'tu-tier-badge--assumed' : ''}`}
      style={{ background: assumed ? 'transparent' : `${color}22`, borderColor: `${color}55`, color }}
      title={title}
    >
      {label}
    </span>
  );
}

export function NeedsTriageBadge() {
  return <DSBadge variant="warning" size="sm" leadingIcon={<DSIcon name="info" />}>Needs Triage</DSBadge>;
}

export function AssigneePill({ name }) {
  if (!name) {
    return <span className="tu-assignee-pill tu-assignee-pill--empty">Unassigned</span>;
  }
  return <span className="tu-assignee-pill">{name}</span>;
}

export function ActionBtn({ label, variant = 'default', onClick, disabled, type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`tu-action-btn tu-action-btn--${variant}`}
    >
      {label}
    </button>
  );
}

export function TicketRow({ issue, onPress, viewerTier, viewerIsAdmin, showMachineName = true }) {
  const needsTriage = isNeedsTriage(issue);
  const qualifies = viewerQualifies(issue, viewerTier, viewerIsAdmin);
  const isResolved = issue.status === 'resolved';

  return (
    <button
      type="button"
      className={`tu-ticket-row ${isResolved ? 'tu-ticket-row--resolved' : ''}`}
      onClick={() => onPress(issue)}
    >
      <div className="tu-ticket-row__top">
        <div className="tu-ticket-row__main">
          {showMachineName && <p className="tu-ticket-row__machine">{issue.machine_name}</p>}
          <p className="tu-ticket-row__desc">{issue.description}</p>
        </div>
        <ImpactBadge impact={issue.impact} />
      </div>
      <div className="tu-ticket-row__bottom">
        <div className="tu-ticket-row__bottom-left">
          {isResolved && <span className="tu-ticket-row__resolved"><DSIcon name="check" /> Resolved</span>}
          {needsTriage ? (
            <>
              <NeedsTriageBadge />
              <TierBadge tier={DEFAULT_TRIAGE_TIER} assumed />
            </>
          ) : (
            <TierBadge tier={issue.required_tech_tier} />
          )}
          <AssigneePill name={issue.assigned_to_name} />
        </div>
        <div className="tu-ticket-row__bottom-right">
          {!needsTriage && !isResolved && !qualifies && !viewerIsAdmin && viewerTier >= 3 && (
            <span className="tu-ticket-row__lock" title="You're not qualified for this tier"><DSIcon name="lock" /></span>
          )}
          <span className="tu-ticket-row__time">{relativeTime(issue.created_at)}</span>
        </div>
      </div>
    </button>
  );
}
